import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import UserAction from "../actions/UserAction";
import { Button, InputField, SelectField } from "../components/ui";
import { LANGUAGE_OPTIONS, TIMEZONE_OPTIONS } from "../ai-content/constants/timezoneConstants";
import LayoutAction from "../actions/LayoutAction";
import { ImageOff } from "lucide-react";
import { getBaseUrl } from "../api/api";

function splitFullName(fullname) {
  const parts = fullname.trim().split(/\s+/);
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
}

function combineFullName(firstName, lastName) {
  return `${firstName} ${lastName}`.replace(/\s+/g, " ").trim();
}

const ProfileSetting = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));

  const displayName = currentUser.get("displayName") || "";
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  useEffect(() => {
    const { firstName, lastName } = splitFullName(displayName);
    setFirstName(firstName);
    setLastName(lastName);
  }, [displayName]);

  const handleSave = async () => {
    await dispatch(
      UserAction.updateCurrentUserSetting({
        currentInboxAppId: currentUser.get("currentInboxAppId"),
        displayName: combineFullName(firstName, lastName),
        language: currentUser.get("language"),
        tz: currentUser.get("tz"),
      })
    );
    dispatch(
      LayoutAction.setNotification({
        title: t("User Setting"),
        type: "success",
        message: t("Updated user setting!"),
      })
    );
  };

  const renderIconEmail = () => {
    if (currentUser.get("currentInboxAppId") === "google") {
      return (
        <img alt="Google" src={`${getBaseUrl()}/static/images/google.png`} className="size-5" />
      );
    }
    if (currentUser.get("currentInboxAppId") === "azure") {
      return (
        <img
          alt="Microsoft"
          src={`${getBaseUrl()}/static/images/microsoft.png`}
          className="size-5"
        />
      );
    }
    return null;
  };

  return (
    <div className="p-4 max-w-5xl">
      <div className="px-4 sm:px-0">
        <h3 className="text-base/7 font-semibold text-gray-900">{t("User Profile")}</h3>
        <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">
          {t("Manage your personal details")}
        </p>
      </div>
      <div className="mt-6">
        <div className="mb-4 w-full rounded-lg bg-[#EFF6FF]">
          <div className="p-4 flex items-center min-w-0 gap-x-4">
            {currentUser.get("avatarUrl") ? (
              <img
                className="h-16 w-16 flex-none rounded-full bg-gray-50"
                src={currentUser.get("avatarUrl")}
                alt={currentUser.get("displayName")}
              />
            ) : (
              <div className="h-16 w-16 rounded-full bg-gray-200 justify-items-center content-center">
                <ImageOff className="text-gray-600" />
              </div>
            )}
            <div className="min-w-0 flex-auto">
              <p className="text-sm font-semibold leading-6 text-gray-900">
                {t("Profile Picture")}
              </p>
              <p className="text-xs leading-6 text-gray-500">
                {t("We only support PNGs, JPEGs and GIFs under 10MB")}
              </p>
              {/* <div className="flex items-center gap-4 mt-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleUploadAvatar}
                  leftIcon={<Camera className="w-4 h-4" />}
                >
                  {t("Upload")}
                </Button>
                <div
                  onClick={handleDeleteAvatar}
                  className="text-red-600 cursor-pointer"
                >
                  <Trash2 className="w-4 h-4" />
                </div>
              </div> */}
            </div>
          </div>
        </div>
        <div className="mb-4">
          <InputField
            label={t("First Name")}
            value={firstName}
            onChange={(value) => {
              setFirstName(value);
            }}
          />
        </div>
        <div className="mb-4">
          <InputField
            label={t("Last Name")}
            value={lastName}
            onChange={(value) => {
              setLastName(value);
            }}
          />
        </div>
        <div className="mb-4 flex items-center">
          <InputField
            label={t("Email")}
            value={currentUser.get("email")}
            disabled
            rightIcon={renderIconEmail()}
          />
        </div>
        <div className="mb-4">
          <InputField
            label={t("Role")}
            value={currentUser.get("admin") ? t("Admin") : t("User")}
            disabled
          />
        </div>
        <div className="mb-4">
          <SelectField
            label={t("Language")}
            value={LANGUAGE_OPTIONS.find(
              (option) => option.value === (currentUser.get("language") || "en")
            )}
            onChange={(opt: any) => {
              dispatch(
                UserAction.setCurrentUser({
                  ...currentUser.toJS(),
                  language: opt?.value || "en",
                })
              );
            }}
            options={LANGUAGE_OPTIONS}
            placeholder={t("Select language")}
          />
        </div>
        <div className="mb-4">
          <SelectField
            label={t("Timezone")}
            value={TIMEZONE_OPTIONS.find((item) => item.value === currentUser.get("tz"))}
            onChange={(opt: any) => {
              dispatch(
                UserAction.setCurrentUser({
                  ...currentUser.toJS(),
                  tz: opt?.value || "",
                })
              );
            }}
            options={TIMEZONE_OPTIONS}
            placeholder={t("Select timezone")}
          />
        </div>
        <div>
          <Button variant="primary" onClick={handleSave}>
            {t("Save")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileSetting;
